import { User, UserManager, WebStorageStateStore } from "oidc-client-ts";

import { config } from "@inflo/config";
import { BuiltFilePaths } from "@inflo/types";

export const INFLO_SUPER_USER = "inf_super";

export class Auth {
  private static hasLimitedAccess = false;

  static readonly userManager = new UserManager({
    authority: config.AUTH_URL,
    client_id: config.CLIENT_ID,
    userStore: new WebStorageStateStore({ store: window.localStorage }), // persist user in local storage
    redirect_uri: new URL(BuiltFilePaths.SignInRedirect, window.location.origin).toString(), // redirect to auth-redirect component
    silent_redirect_uri: new URL(BuiltFilePaths.SilentRenew, window.location.origin).toString(),
    response_type: "code",
    scope: "openid profile email offline_access inflo_apiserver ApiPublic AccountingDataExport",
    loadUserInfo: true,
    monitorSession: false,
    monitorAnonymousSession: true,
    filterProtocolClaims: true,
    revokeTokensOnSignout: true,
    automaticSilentRenew: true,
  });

  static get infloUserManager() {
    return this.userManager;
  }

  static async startSignIn() {
    try {
      await this.userManager.signinRedirect();
    } catch (err) {
      console.error(err);
    }
  }

  static async completeSignIn() {
    try {
      return await this.userManager.signinCallback();
    } catch (err) {
      console.error(err);
      return null;
    }
  }

  static async startSignOut() {
    try {
      return await this.userManager.signoutRedirect();
    } catch (err) {
      console.error(err);
    }
  }

  static async storeUser(user: User) {
    return this.userManager.storeUser(user);
  }

  static async getUser() {
    return this.userManager.getUser();
  }

  static async removeUser() {
    return this.userManager.removeUser();
  }

  static async checkIfHasLimitedAccess(user?: User) {
    if (!user) {
      user = await Auth.getUser();
    }

    if (!user) {
      return false;
    }

    // TODO: Auth.decodeJwtToken might be replaced with infloUserManager by checking if the user already contains this property
    const jwtResponse = Auth.decodeJwtToken(user.access_token);

    if (!jwtResponse) {
      return false;
    }

    this.hasLimitedAccess = INFLO_SUPER_USER in jwtResponse;

    return this.hasLimitedAccess;
  }

  static get isAccessLimited() {
    return this.hasLimitedAccess;
  }

  static decodeJwtToken(token: string): Record<string, any> | null {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );

      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("Invalid token", error);
      return null;
    }
  }
}
